import React, { useState } from 'react';
import Axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import SPAButton from '../../shared/SPAButton';
import SPADialog from '../../shared/SPADialog';
import SPAInput from '../../shared/SPAInput';
import { ProgramUnit } from './types';

interface UnitNumberDialogProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  programId: number;
  unit: ProgramUnit;
}

const labelClass = 'tw-flex tw-flex-col tw-gap-1 tw-text-sm tw-font-medium tw-text-spa-primary';

const isValidNumber = (value: string) => /^\d+$/.test(value.trim());

// The unit's number lives on its unit-level program_lesson (lesson_id 0), the same record the
// legacy UnitNumberModal patched. Units are refetched afterwards so display_number and
// title_with_position come back from the server rather than being rebuilt here.
const UnitNumberDialog = ({ onOpenChange, open, programId, unit }: UnitNumberDialogProps) => {
  const initialValue = () => String(unit.title_number ?? unit.display_number);
  const [value, setValue] = useState(initialValue);
  const queryClient = useQueryClient();
  const titleId = `unit-${unit.id}-title`;
  const numberId = `unit-${unit.id}-number`;

  const save = useMutation({
    mutationFn: (titleNumber: string) => Axios.patch(
      Routes.plato_api_program_lesson_path(unit.unit_program_lesson_id),
      { program_lesson: { title_number: titleNumber } },
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.ProgramUnits, programId] });
      onOpenChange(false);
    },
  });

  const handleOpenChange = (next: boolean) => {
    if (next) setValue(initialValue());
    else save.reset();
    onOpenChange(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidNumber(value)) save.mutate(value.trim());
  };

  return (
    <SPADialog open={open} onOpenChange={handleOpenChange}>
      <SPADialog.Content size="sm">
        <form onSubmit={handleSubmit}>
          <SPADialog.Header>
            <SPADialog.Title>Edit Unit Number</SPADialog.Title>
            <SPADialog.CloseButton />
          </SPADialog.Header>
          <SPADialog.Body className="tw-flex tw-flex-col tw-gap-4">
            <label className={labelClass} htmlFor={titleId}>
              Title
              <SPAInput disabled id={titleId} value={unit.title} />
            </label>
            <label className={labelClass} htmlFor={numberId}>
              Unit Number
              <SPAInput
                required
                id={numberId}
                inputMode="numeric"
                min={0}
                type="number"
                value={value}
                onChange={e => setValue(e.target.value)}
              />
            </label>
            {save.isError && (
              <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-spa-error" role="alert">
                The unit number could not be saved. Try again.
              </p>
            )}
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPAButton>Cancel</SPAButton>
            </SPADialog.Close>
            <SPAButton disabled={!isValidNumber(value) || save.isLoading} type="submit" variant="primary">
              Save
            </SPAButton>
          </SPADialog.Footer>
        </form>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default UnitNumberDialog;
